/**
 * Telegram CloudStorage API types
 */
export interface TelegramCloudStorage {
  setItem(key: string, value: string, callback?: (error: Error | null, success: boolean) => void): void;
  getItem(key: string, callback: (error: Error | null, value?: string) => void): void;
  getItems(keys: string[], callback: (error: Error | null, values?: Record<string, string>) => void): void;
  removeItem(key: string, callback?: (error: Error | null, success: boolean) => void): void;
  removeItems(keys: string[], callback?: (error: Error | null, success: boolean) => void): void;
  getKeys(callback: (error: Error | null, keys?: string[]) => void): void;
}

export interface TelegramWebApp {
  initData: string;
  platform?: string;
  isFullscreen?: boolean;
  ready(): void;
  isVersionAtLeast?(version: string): boolean;
  requestFullscreen?(): void;
  onEvent?(eventType: string, handler: () => void): void;
  CloudStorage: TelegramCloudStorage;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

/**
 * Получить initData от Telegram Mini App.
 * initData содержит информацию о пользователе и подписывается Telegram.
 */
export function getTelegramMiniAppInitData(): string | undefined {
  // MOCK для тестирования в обычном браузере (удалите в продакшене!)
  if (import.meta.env.TG_APP_ENV === 'development' && !(window as any).Telegram?.WebApp) {
    console.warn('[getTelegramMiniAppInitData] Using MOCK initData for development');
    return 'mock_init_data_for_testing';
  }

  return (window as any).Telegram?.WebApp?.initData;
}

/**
 * Проверить доступность Telegram Mini App API.
 * Возвращает true если приложение запущено в контексте Mini App.
 */
export function hasValidMiniAppEnvironment(): boolean {
  return Boolean((window as any).Telegram?.WebApp?.initData);
}

/**
 * Уведомить Telegram о готовности Mini App.
 * Должно вызываться после завершения инициализации.
 */
export function notifyTelegramMiniAppReady(): void {
  (window as any).Telegram?.WebApp?.ready?.();
}

// На десктопных клиентах полноэкранного режима нет - `requestFullscreen` там
// отвечает ошибкой FULLSCREEN_UNSUPPORTED, поэтому просим его только на телефонах
const FULLSCREEN_PLATFORMS = new Set(['android', 'ios']);
const FULLSCREEN_MIN_VERSION = '8.0';
const FULLSCREEN_BODY_CLASS = 'is-tg-fullscreen';
/**
 * Развернуть Mini App на весь экран. Только на телефонах: на компьютере
 * приложение должно открываться обычным окном.
 */
export function requestTelegramMiniAppFullscreen(): void {
  const webApp = window.Telegram?.WebApp;
  if (!webApp?.platform || !FULLSCREEN_PLATFORMS.has(webApp.platform)) return;
  if (!webApp.isVersionAtLeast?.(FULLSCREEN_MIN_VERSION) || !webApp.requestFullscreen) return;

  // Отступы под системные и телеграмовские контролы берёт на себя CSS: величины
  // лежат в переменных `--tg-safe-area-inset-*`, которые проставляет сам клиент
  const syncFullscreenClass = () => {
    document.body.classList.toggle(FULLSCREEN_BODY_CLASS, Boolean(webApp.isFullscreen));
  };

  webApp.onEvent?.('fullscreenChanged', syncFullscreenClass);
  webApp.onEvent?.('fullscreenFailed', syncFullscreenClass);

  webApp.requestFullscreen();
  // Клиент подтверждает режим событием, но разметка успевает отрисоваться раньше него
  document.body.classList.add(FULLSCREEN_BODY_CLASS);
}

/**
 * Проверить доступность CloudStorage API.
 * Возвращает true если CloudStorage доступен.
 */
export function isCloudStorageAvailable(): boolean {
  return typeof window !== 'undefined'
    && typeof window.Telegram?.WebApp?.CloudStorage !== 'undefined'
    && typeof window.Telegram?.WebApp?.CloudStorage?.setItem === 'function';
}

/**
 * Получить CloudStorage API.
 * Возвращает undefined если API недоступен.
 */
export function getCloudStorageApi(): TelegramCloudStorage | undefined {
  return window.Telegram?.WebApp?.CloudStorage;
}
